import clsx from 'clsx'
import { format } from 'date-fns'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath } from 'react-router-dom'
import { Link } from 'react-router-dom'

import Avatar from '@/components-latest/primitives/Avatar'
import Text from '@/components-latest/primitives/Text'
import { formatPrice } from '@/components-latest/ui/OrdersPage/PaymentSummary'
import TimerTag from '@/components-latest/ui/OrdersPage/TImerTag/TimerTag'
import { PATHS } from '@/config/router/routes'
import { SCROLL_ORDER_QUERY_ID } from '@/constants/common'
import { IOrderDetails } from '@/ducks/orders/orders.types'
import {
  selectCurrencyCode,
  selectStoreId
} from '@/ducks/store/selectors/store.selectors'
import { logFirebaseEvent } from '@/utils/firebase.utils'

import Styles from '../Toaster.module.scss'

interface INewOrderToastProps {
  order: IOrderDetails & { earnings: number }
  onTimerEnd: () => void
}

const NewOrderToast: React.FC<INewOrderToastProps> = ({
  order,
  onTimerEnd
}) => {
  const { t } = useTranslation()
  const storeId = useSelector(selectStoreId)

  const currency = useSelector(selectCurrencyCode) || ''

  return (
    <Link
      to={{
        pathname: generatePath(PATHS.ORDERS_BY_STATUS, { status: 'new' }),
        search: `?${SCROLL_ORDER_QUERY_ID}=${order.order_id}`
      }}
      className={clsx(Styles.newOrderToast)}
      onClick={() => {
        logFirebaseEvent('AC_Web_NewOrderNotifBanner_Click', {
          Event_Time: format(new Date(), 'dd/MM/yyyy - HH:mm:ss'),
          Store_ID: storeId,
          Order_ID: order.order_id
        })
      }}
      data-testid={`new-order-toast-${order.order_id}`}>
      <div className={Styles.header}>
        <Text variant="nextTitle-4">New order</Text>
        <TimerTag
          timeLeft={order.time_left}
          totalTime={order.time_out}
          onTimerEnd={onTimerEnd}
        />
      </div>

      <div className={Styles.content}>
        <div className={Styles.user}>
          <Avatar
            size="small"
            string={order.user.username}
            image={order.user.avatar}
          />
          <div>
            <Text
              className={Styles.userType}
              variant="nextOverline"
              weight="bold"
              gutter={4}>
              {t('orders.user-types.client')}
            </Text>
            <Text variant="nextBody" weight="bold">
              {order.user.username}
            </Text>
          </div>
        </div>

        <div>
          <Text variant="nextBody" weight="bold">
            {t('orders.count-items', {
              count: order.foods.reduce((acc, food) => acc + food.quantity, 0)
            })}
          </Text>
        </div>

        <div className={Styles.total}>
          <Text className={Styles.totalLabel} variant="nextBody">
            {t('orders.total')}
          </Text>

          <Text variant="nextBody" weight="bold">
            {formatPrice(order.earnings || 0, currency, {
              signDisplay: 'never'
            })}
          </Text>
        </div>
      </div>
    </Link>
  )
}

export default NewOrderToast
